// src/pages/EnqueteCommerceTransportEntreprise.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Container,
    Typography,
    Grid,
    Card,
    Button,
    FormControl,
    Select,
    MenuItem,
    Box,
    CardMedia,
    Tabs,
    Tab,
    createTheme,
} from '@mui/material';
import Footer from '../navbar/Footer';
import moment from 'moment';
import { getPublications } from '../services/publicationService';

const theme = createTheme({
    palette: {
        primary: { main: '#388E3C' },
        secondary: { main: '#673AB7' },
        background: { default: '#f5f5f5' },
    }
});

function EnqueteCommerceTransportEntreprise() {
    const [activeTab, setActiveTab] = useState(0);

    // Données
    const [commerceData, setCommerceData] = useState([]);
    const [transportData, setTransportData] = useState([]);
    const [entreprisesData, setEntreprisesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtres par année
    const [commerceYear, setCommerceYear] = useState('');
    const [transportYear, setTransportYear] = useState('');
    const [entreprisesYear, setEntreprisesYear] = useState('');

    // Pagination (nombre de rapports affichés)
    const [commerceCount, setCommerceCount] = useState(2);
    const [transportCount, setTransportCount] = useState(2);
    const [entreprisesCount, setEntreprisesCount] = useState(2);

    // Chargement des données depuis l'API
    useEffect(() => {
        setLoading(true);
        Promise.all([
            getPublications({ type: 'rec', perPage: 1000 }), // commerce
            getPublications({ type: 'ret', perPage: 1000 }), // transport
            getPublications({ type: 'ree', perPage: 1000 })   // entreprises
        ])
            .then(([resCommerce, resTransport, resEntreprises]) => {
                if (resCommerce.error) setError(resCommerce.error);
                else if (resTransport.error) setError(resTransport.error);
                else if (resEntreprises.error) setError(resEntreprises.error);
                else {
                    // Mapper commerce
                    const mappedCommerce = resCommerce.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        image: item.image_url,
                        description: item.description,
                        date: new Date(item.date)
                    }));
                    // Mapper transport
                    const mappedTransport = resTransport.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        image: item.image_url,
                        description: item.description,
                        date: new Date(item.date)
                    }));
                    // Mapper entreprises
                    const mappedEntreprises = resEntreprises.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        image: item.image_url,
                        description: item.description,
                        date: new Date(item.date)
                    }));

                    // Tri par date décroissante
                    mappedCommerce.sort((a, b) => b.date - a.date);
                    mappedTransport.sort((a, b) => b.date - a.date);
                    mappedEntreprises.sort((a, b) => b.date - a.date);

                    setCommerceData(mappedCommerce);
                    setTransportData(mappedTransport);
                    setEntreprisesData(mappedEntreprises);
                }
            })
            .catch(err => {
                setError('Erreur lors du chargement des données.');
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, []);

    // Tous les années disponibles (union des trois jeux)
    const allYears = useMemo(() => {
        const years = new Set();
        [...commerceData, ...transportData, ...entreprisesData].forEach(item => {
            years.add(item.date.getFullYear());
        });
        return [...years].sort((a, b) => b - a);
    }, [commerceData, transportData, entreprisesData]);

    // Filtrage des données pour chaque onglet
    const filteredCommerce = useMemo(() => {
        if (!commerceYear) return commerceData;
        return commerceData.filter(item => item.date.getFullYear().toString() === commerceYear);
    }, [commerceData, commerceYear]);

    const filteredTransport = useMemo(() => {
        if (!transportYear) return transportData;
        return transportData.filter(item => item.date.getFullYear().toString() === transportYear);
    }, [transportData, transportYear]);

    const filteredEntreprises = useMemo(() => {
        if (!entreprisesYear) return entreprisesData;
        return entreprisesData.filter(item => item.date.getFullYear().toString() === entreprisesYear);
    }, [entreprisesData, entreprisesYear]);

    const handleChangeTab = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleCommerceYearChange = (e) => setCommerceYear(e.target.value);
    const handleTransportYearChange = (e) => setTransportYear(e.target.value);
    const handleEntreprisesYearChange = (e) => setEntreprisesYear(e.target.value);

    if (loading) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px', paddingInline: '20px' }}>
                    <Typography variant="body1">Chargement des données...</Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px', paddingInline: '20px' }}>
                    <Typography variant="body1" color="error">{error}</Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
            <NavigationBar />
            <Container maxWidth="lg" style={{ paddingTop: '54px', paddingInline: '20px' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleChangeTab}
                    textColor="inherit"
                    indicatorColor="secondary"
                >
                    <Tab label="Commerce" />
                    <Tab label="Transport" />
                    <Tab label="Entreprises" />
                </Tabs>

                {/* Section Commerce */}
                {activeTab === 0 && (
                    <>
                        <Typography variant="h4" align="justify" gutterBottom style={{ color: '#2E7D32' }}>
                            Commerce
                        </Typography>
                        <Typography variant="body1" align="justify" paragraph>
                            Cette section présente les rapports d'enquête sur le commerce transfrontalier informel au Burundi. Ces documents fournissent une analyse détaillée des volumes d'échanges, des produits concernés et des principales tendances commerciales, offrant ainsi une vision approfondie des dynamiques économiques qui influencent le secteur commercial dans le pays.
                        </Typography>
                        <FormControl variant="outlined" fullWidth style={{ marginLeft: '0px', width: '300px', marginBottom: 20 }}>
                            <Select
                                value={commerceYear}
                                onChange={handleCommerceYearChange}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Tous les rapports</em>
                                </MenuItem>
                                {allYears.map(year => (
                                    <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Grid container spacing={1} style={{ marginTop: '10px' }}>
                            {filteredCommerce.slice(0, commerceCount).map((bulletin, index) => (
                                <Grid item xs={12} sm={6} md={12} key={index}>
                                    <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                                        <Box sx={{ display: 'flex' }}>
                                            <CardMedia
                                                component="img"
                                                sx={{ width: 91, height: 140, m: 2, cursor: 'pointer' }}
                                                image={bulletin.image}
                                                alt={bulletin.title}
                                                onClick={() => window.open(bulletin.link, '_blank')}
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                                                <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#388E3C' }}>
                                                    <a
                                                        href={bulletin.link}
                                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <DownloadIcon /> {bulletin.title}
                                                    </a>
                                                </Typography>
                                                <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                                                    {bulletin.description}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Publié en {moment(bulletin.date).format('MMMM YYYY')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        {commerceCount < filteredCommerce.length && (
                            <Button
                                onClick={() => setCommerceCount(prev => prev + 2)}
                                style={{
                                    marginTop: '20px',
                                    marginBottom: '20px',
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                }}
                            >
                                Plus de rapports
                            </Button>
                        )}
                    </>
                )}

                {/* Section Transport */}
                {activeTab === 1 && (
                    <>
                        <Typography variant="h4" align="justify" gutterBottom style={{ color: '#2E7D32' }}>
                            Transport
                        </Typography>
                        <Typography variant="body1" align="justify" paragraph>
                            Cette section présente les rapports d'enquête relatifs au secteur des transports au Burundi. Ces documents mettent en lumière les différents modes de transport (formels et informels), leur impact sur l'économie locale, et les défis auxquels le secteur est confronté. Ils permettent de mieux comprendre le rôle crucial des infrastructures de transport dans le développement économique du pays.
                        </Typography>
                        <FormControl variant="outlined" fullWidth style={{ marginLeft: '0px', width: '300px', marginBottom: 20 }}>
                            <Select
                                value={transportYear}
                                onChange={handleTransportYearChange}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Tous les rapports</em>
                                </MenuItem>
                                {allYears.map(year => (
                                    <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Grid container spacing={1} style={{ marginTop: '10px' }}>
                            {filteredTransport.slice(0, transportCount).map((bulletin, index) => (
                                <Grid item xs={12} sm={6} md={12} key={index}>
                                    <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                                        <Box sx={{ display: 'flex' }}>
                                            <CardMedia
                                                component="img"
                                                sx={{ width: 91, height: 140, m: 2, cursor: 'pointer' }}
                                                image={bulletin.image}
                                                alt={bulletin.title}
                                                onClick={() => window.open(bulletin.link, '_blank')}
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                                                <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#388E3C' }}>
                                                    <a
                                                        href={bulletin.link}
                                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <DownloadIcon /> {bulletin.title}
                                                    </a>
                                                </Typography>
                                                <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                                                    {bulletin.description}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Publié en {moment(bulletin.date).format('MMMM YYYY')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        {transportCount < filteredTransport.length && (
                            <Button
                                onClick={() => setTransportCount(prev => prev + 2)}
                                style={{
                                    marginTop: '20px',
                                    marginBottom: '20px',
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                }}
                            >
                                Plus de rapports
                            </Button>
                        )}
                    </>
                )}

                {/* Section Entreprises */}
                {activeTab === 2 && (
                    <>
                        <Typography variant="h4" align="justify" gutterBottom style={{ color: '#2E7D32' }}>
                            Entreprises
                        </Typography>
                        <Typography variant="body1" align="justify" paragraph>
                            Cette section présente les rapports d'enquête sur les entreprises, mettant en avant les investissements directs étrangers, les statistiques sur les entreprises et d'autres études économiques pertinentes. Ces rapports sont conçus pour fournir des informations précieuses aux investisseurs et aux décideurs, facilitant ainsi une meilleure compréhension des opportunités d'affaires et des défis rencontrés dans le tissu économique burundais.
                        </Typography>
                        <FormControl variant="outlined" fullWidth style={{ marginLeft: '0px', width: '300px', marginBottom: 20 }}>
                            <Select
                                value={entreprisesYear}
                                onChange={handleEntreprisesYearChange}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Tous les rapports</em>
                                </MenuItem>
                                {allYears.map(year => (
                                    <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Grid container spacing={1} style={{ marginTop: '10px' }}>
                            {filteredEntreprises.slice(0, entreprisesCount).map((document, index) => (
                                <Grid item xs={12} sm={6} md={12} key={index}>
                                    <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                                        <Box sx={{ display: 'flex' }}>
                                            <CardMedia
                                                component="img"
                                                sx={{ width: 91, height: 140, m: 2, cursor: 'pointer' }}
                                                image={document.image}
                                                alt={document.title}
                                                onClick={() => window.open(document.link, '_blank')}
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                                                <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#388E3C' }}>
                                                    <a
                                                        href={document.link}
                                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <DownloadIcon /> {document.title}
                                                    </a>
                                                </Typography>
                                                <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                                                    {document.description}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Publié en {moment(document.date).format('MMMM YYYY')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        {entreprisesCount < filteredEntreprises.length && (
                            <Button
                                onClick={() => setEntreprisesCount(prev => prev + 2)}
                                style={{
                                    marginTop: '20px',
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                }}
                            >
                                Plus de rapports
                            </Button>
                        )}
                    </>
                )}
            </Container>
            <br />
            <br />
            <Footer />
        </div>
    );
}

export default EnqueteCommerceTransportEntreprise;